/**
 * AutofillPicksButton — Picks tab control that reveals + adopts the user's still-editable
 * auto-assigned (monkey) match-score picks in one tap. Shown only when there is at least one
 * such pick. Presentational: confirmation + the API call live in PicksView.
 */

import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Sparkles } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getAutofillPicksButtonStyles } from './AutofillPicksButton.styles';
import { AUTOFILL_PICKS_COPY, AUTOFILL_PICKS_TEST_ID } from './PicksView.constants';

interface AutofillPicksButtonProps {
  count: number;
  isBusy: boolean;
  onPress: () => void;
}

export const AutofillPicksButton: React.FC<AutofillPicksButtonProps> = ({
  count,
  isBusy,
  onPress,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getAutofillPicksButtonStyles(theme), [theme]);

  return (
    <View style={styles.wrap}>
      <Pressable
        testID={AUTOFILL_PICKS_TEST_ID}
        accessibilityRole="button"
        accessibilityLabel={AUTOFILL_PICKS_COPY.TITLE}
        disabled={isBusy}
        onPress={onPress}
        style={({ pressed, hovered }) => [
          styles.button,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          isBusy && styles.buttonBusy,
          pressed && !isBusy && styles.buttonPressed,
          hovered && Platform.OS === 'web' && !isBusy && styles.buttonHover,
        ]}
      >
        <View style={styles.iconBadge}>
          {isBusy ? (
            <ActivityIndicator size="small" color={theme.onPrimary} />
          ) : (
            <Sparkles size={18} color={theme.onPrimary} strokeWidth={2.4} />
          )}
        </View>
        <View style={styles.textBlock}>
          <Text style={styles.title}>{AUTOFILL_PICKS_COPY.TITLE}</Text>
          <Text style={styles.subtitle}>{AUTOFILL_PICKS_COPY.SUBTITLE(count)}</Text>
        </View>
      </Pressable>
    </View>
  );
};
